/**
 * Offline sync service.
 *
 * Flushes pending expenses from IndexedDB when connectivity returns.
 * Distinguishes permanent failures (4xx — bad data) from transient
 * failures (5xx / network — worth retrying).
 */

import {
  getAllPendingExpenses,
  removePendingExpense,
  updatePendingExpense,
} from './offlineDb';
import { transactionsApi } from './api';
import { AxiosError } from 'axios';

export interface SyncResult {
  synced: number;
  /** Transient failures — will be retried next time. */
  retriable: number;
  /** Permanent failures — marked as 'error' in IDB. */
  permanent: number;
}

function isPermanentFailure(err: unknown): boolean {
  if (err instanceof AxiosError && err.response) {
    const status = err.response.status;
    return status >= 400 && status < 500;
  }
  return false;
}

/**
 * Attempt to flush all queued expenses to the server.
 *
 * - On success: removes the record from IDB.
 * - On 4xx (permanent): marks the record as `error` so the user can
 *   inspect / delete it via the FailedExpensesBanner.
 * - On 5xx / network (transient): leaves the record as `pending`
 *   so it can be retried next time.
 */
export async function syncPendingExpenses(): Promise<SyncResult> {
  const pending = await getAllPendingExpenses();
  const result: SyncResult = { synced: 0, retriable: 0, permanent: 0 };

  const syncable = pending.filter((p) => p.status !== 'error');

  for (const item of syncable) {
    try {
      item.status = 'syncing';
      await updatePendingExpense(item);

      await transactionsApi.createSplitExpense(item.payload);

      if (item.localId != null) {
        await removePendingExpense(item.localId);
      }
      result.synced++;
    } catch (err) {
      item.retryCount++;

      if (isPermanentFailure(err)) {
        item.status = 'error';
        item.lastError =
          err instanceof AxiosError
            ? (err.response?.data as { message?: string })?.message ??
              err.message
            : 'Unknown validation error';
        result.permanent++;
      } else {
        item.status = 'pending';
        item.lastError =
          err instanceof Error ? err.message : 'Network error';
        result.retriable++;
      }

      await updatePendingExpense(item);
    }
  }

  return result;
}
